import {
  DialogContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import React from "react";
import { IStoreState } from "../../../redux/initialStoreState";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { ILead, IMultipleEvent } from "../../../redux/leads/leads.types";
import { useSelector } from "react-redux";
import { formatAdditionalContacts } from "../helpers/leadHelpers";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";

export interface IPreviewBeforeSaveDialogProps {
  open: boolean;
  data: ILead | null;
  onClose: () => void;
  onSave: () => void;
  hideMakePayment?: boolean;
}

export const PreviewBeforeSaveDialog: React.FC<
  IPreviewBeforeSaveDialogProps
> = (props) => {
  const { open, onClose, onSave, data } = props;

  const { ftDate } = useTimezoneFormatter();
  const { data: requestedLeadData } = useSelector(
    (storeState: IStoreState) => storeState.leads.lead,
  );

  const toTitleCase = (str: string) =>
    str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
    );

  // extras is stored as { event_title: "..." } (or a JSON string of the same) — pull out
  // just the title so the diff shows readable text instead of "[object Object]"
  const getExtrasTitle = (value: unknown): string => {
    let obj: Record<string, string> = {};
    if (value && typeof value === "object" && !Array.isArray(value)) {
      obj = value as Record<string, string>;
    } else if (typeof value === "string" && value.trim() !== "") {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          obj = parsed;
        }
      } catch { }
    }
    return obj.event_title || "";
  };

  const compareData = () => {
    if (!data || !requestedLeadData) return { commonChanges: [], eventChanges: [] };

    const commonDifferences: any[] = [];
    const eventDifferences: any[] = [];
    const fields = Object.keys(data);

    const excludedFields = [
      "event_name",
      "event_timing_uuid",
      "whatsapp_status",
      "company_outlet_name",
      "is_replied",
      "preferred_venue_uuid",
      "handled_by_employee_uuid",
      "event_timing_uuid",
      "event_time_uuid",
      "master_status_uuid",
      "drive_status_uuid",
      "derived_status",
      "multiple_events_list",
      "remarks",
      "is_archived",
      // Per-event `status` is diffed → shows as "status" in the table; `actual_status` is omitted here.
      // ManageLead save→payment gate uses updated form `status` / `actual_status` (BOOKED/CANCELLED/DEAD; see findEventForPaymentAfterSavePreview).
      "actual_status",
      "insert_ts",
      "booking_date",
      "show_month_picker"
    ];

    const fieldNameMapping: { [key: string]: string } = {
      preferred_venue: "Preferred Venue",
      handled_by_employee: "Handled By Employee",
      master_status_uuid: "Status",
      event_type: "Event Type",
      event_time: "Event Time",
      function_month: "Event Month",
      function_date: "Event Date",
      followup_date: "Followup Date",
      number_of_guests: "Number of Guest",
      number_of_rooms: "Number of Rooms",
      payment_mode: "Payment Mode",
      cancellation_dead_reason: "Cancellation Reason",
      other_event_name: "Event Name",
      extras: "Custom Title",
      relation_with_customer: "Relation with Customer",
      customer_relative_name: "Customer Relative Name",
      additional_contact_number: "Additional Contact Details",
    };

    // =============== Common fields comparison  ===============

    for (const field of fields) {
      if (excludedFields.includes(field)) continue;
      if (field.toLowerCase().includes("uuid")) continue;

      const oldValue = requestedLeadData[field as keyof ILead];
      const newValue = data[field as keyof ILead];

      if (field === "additional_contact_number") {
        const oldContacts = requestedLeadData.additional_contact_number || [];
        const newContacts = data.additional_contact_number || [];
        const maxLen = Math.max(oldContacts.length, newContacts.length);

        for (let i = 0; i < maxLen; i++) {
          const oldContact = oldContacts[i];
          const newContact = newContacts[i];
          const suffix = maxLen > 1 ? ` (Contact ${i + 1})` : "";

          if (oldContact && newContact) {
            // Check Contact Number
            const oldPhone = [oldContact.country_code, oldContact.contact_number]
              .filter(Boolean)
              .join(" ")
              .trim();
            const newPhone = [newContact.country_code, newContact.contact_number]
              .filter(Boolean)
              .join(" ")
              .trim();
            if (oldPhone !== newPhone) {
              commonDifferences.push({
                field: `Additional Contact Number${suffix}`,
                previous: oldPhone || "---",
                updated: newPhone || "---",
                isNewValuesAdded: !oldPhone,
              });
            }

            // Check Additional Name
            const oldName = (oldContact.name || "").trim();
            const newName = (newContact.name || "").trim();
            if (oldName !== newName) {
              commonDifferences.push({
                field: `Additional Name${suffix}`,
                previous: oldName || "---",
                updated: newName || "---",
                isNewValuesAdded: !oldName,
              });
            }

            // Check Relation with Customer
            const oldRelation = (oldContact.relation_with_customer || "").trim();
            const newRelation = (newContact.relation_with_customer || "").trim();
            if (oldRelation !== newRelation) {
              commonDifferences.push({
                field: `Relation with Customer${suffix}`,
                previous: oldRelation || "---",
                updated: newRelation || "---",
                isNewValuesAdded: !oldRelation,
              });
            }
          } else if (newContact) {
            // New contact added
            const newPhone = [newContact.country_code, newContact.contact_number]
              .filter(Boolean)
              .join(" ")
              .trim();
            const newName = (newContact.name || "").trim();
            const newRelation = (newContact.relation_with_customer || "").trim();

            if (newPhone) {
              commonDifferences.push({
                field: `Additional Contact Number${suffix}`,
                previous: "---",
                updated: newPhone,
                isNewValuesAdded: true,
              });
            }
            if (newName) {
              commonDifferences.push({
                field: `Additional Name${suffix}`,
                previous: "---",
                updated: newName,
                isNewValuesAdded: true,
              });
            }
            if (newRelation) {
              commonDifferences.push({
                field: `Relation with Customer${suffix}`,
                previous: "---",
                updated: newRelation,
                isNewValuesAdded: true,
              });
            }
          } else if (oldContact) {
            // Contact removed
            const oldFormatted = formatAdditionalContacts([oldContact]);
            commonDifferences.push({
              field: `Removed Contact${suffix}`,
              previous: oldFormatted.join(", ") || "---",
              updated: "---",
              isNewValuesAdded: false,
            });
          }
        }
        continue;
      }

      let hasChanged = false;

      if (field === "extras") {
        hasChanged = getExtrasTitle(oldValue) !== getExtrasTitle(newValue);
      } else {
        hasChanged = oldValue !== newValue;
      }

      if (!hasChanged) continue;

      let previousValue: any = oldValue;
      let updatedValue: any = newValue;

      // Date formatting
      if (
        field === "function_date" ||
        field === "date_of_visit" ||
        field === "followup_date"
      ) {
        previousValue = previousValue ? ftDate(previousValue as string) : "---";
        updatedValue = updatedValue ? ftDate(updatedValue as string) : "---";
      }

      // Custom Title (extras) — show the title text, not the raw object
      if (field === "extras") {
        previousValue = getExtrasTitle(oldValue);
        updatedValue = getExtrasTitle(newValue);
      }

      commonDifferences.push({
        field: fieldNameMapping[field] || field,
        previous: previousValue,
        updated: updatedValue,
        isNewValuesAdded:
          !previousValue ||
          previousValue === "---" ||
          (Array.isArray(previousValue) && previousValue.length === 0),
      });
    }

    // =============== Compare multiple_events_list if it exists ===============
    if (data.multiple_events_list && data.multiple_events_list.length > 0) {
      const oldEventsList = requestedLeadData.multiple_events_list || [];
      const newEventsList = data.multiple_events_list || [];

      newEventsList.forEach((newEvent: IMultipleEvent, index: number) => {
        const oldEvent = newEvent.leads_uuid
          ? oldEventsList.find((e) => e.leads_uuid === newEvent.leads_uuid)
          : oldEventsList[index];
        const eventChanges: any[] = [];

        if (!oldEvent) {
          // This is a new event (same pairing as ManageLead `hasNewEventComparedToServer`).
          eventDifferences.push({
            eventName: newEvent.event_type || `Event ${index + 1}`,
            eventIndex: index,
            isNewEvent: true,
            changes: [],
          });
          return;
        }

        // Compare each field in the event
        const eventFields = Object.keys(newEvent);
        for (const field of eventFields) {
          if (excludedFields.includes(field)) continue;
          if (
            field.toLowerCase().includes("uuid") ||
            field.toLowerCase() === "remarks"
          )
            continue;

          const oldValue = oldEvent[field as keyof IMultipleEvent];
          const newValue = newEvent[field as keyof IMultipleEvent];

          // Normalize values for comparison (handle null, undefined, empty strings)
          const normalizeValue = (val: any) => {
            if (val === null || val === undefined) return "";
            if (typeof val === "string") return val.trim();
            return val;
          };

          const normalizedOldValue = normalizeValue(oldValue);
          const normalizedNewValue = normalizeValue(newValue);

          // Compare values (handle arrays and objects with JSON.stringify, simple values directly)
          const hasChanged =
            field === "extras"
              ? getExtrasTitle(oldValue) !== getExtrasTitle(newValue)
              : Array.isArray(normalizedOldValue) ||
                Array.isArray(normalizedNewValue)
                ? JSON.stringify(normalizedOldValue) !==
                JSON.stringify(normalizedNewValue)
                : normalizedOldValue !== normalizedNewValue;

          if (hasChanged) {
            let previousValue: any = oldValue;
            let updatedValue: any = newValue;

            // Format dates
            if (["function_date", "followup_date"].includes(field)) {
              previousValue = previousValue
                ? ftDate(previousValue as string)
                : "---";
              updatedValue = updatedValue
                ? ftDate(updatedValue as string)
                : "---";
            }

            // Format arrays (like service)
            if (Array.isArray(previousValue)) {
              previousValue =
                previousValue.length > 0
                  ? previousValue.map((s: any) => s.name || s).join(", ")
                  : "---";
            }
            if (Array.isArray(updatedValue)) {
              updatedValue =
                updatedValue.length > 0
                  ? updatedValue.map((s: any) => s.name || s).join(", ")
                  : "---";
            }

            // Custom Title (extras) — show the title text, not the raw object
            if (field === "extras") {
              previousValue = getExtrasTitle(oldValue);
              updatedValue = getExtrasTitle(newValue);
            }

            eventChanges.push({
              field: fieldNameMapping[field] || field,
              previous: previousValue || "---",
              updated: updatedValue || "---",
              isNewValuesAdded: previousValue ? false : true,
            });
          }
        }

        if (eventChanges.length > 0) {
          eventDifferences.push({
            eventName: newEvent.event_type || `Event ${index + 1}`,
            eventIndex: index,
            isNewEvent: false,
            changes: eventChanges,
          });
        }
      });
    }

    return { commonChanges: commonDifferences, eventChanges: eventDifferences };
  };

  const { commonChanges, eventChanges } = compareData();

  const hasChanges = commonChanges.length > 0 || eventChanges.length > 0;

  const denseTableSx = {
    "& .MuiTableCell-root": {
      borderBottom: "none",
      py: 0.75,
      px: 1,
    },
    "& .MuiTableRow-root": {
      verticalAlign: "top",
    },
    "& .MuiTableCell-head": {
      fontWeight: 700,
      fontSize: "12px",
      color: "text.secondary",
      py: 0.75,
      px: 1,
      whiteSpace: "nowrap",
    },
  } as const;

  return (
    <Dialog
      open={open}
      title={"Are you sure you want to apply these changes?"}
      onClose={onClose}
      size="md"
      actions={[
        {
          type: "button" as const,
          label: "Cancel",
          variant: "text" as const,
          onClick: onClose,
        },
        {
          type: "submit" as const,
          label: "Apply Changes",
          variant: "contained" as const,
          onClick: onSave,
        },
      ]}
    >
      <DialogContent sx={{ pt: 1.25, pb: 1.5 }}>
        {hasChanges ? (
          <Box>
            {/* Common Fields Changes */}
            {commonChanges.length > 0 && (
              <Box sx={{ mb: 1.75 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    color: "primary.main",
                    fontWeight: 700,
                    fontSize: "15px",
                    lineHeight: 1.2,
                  }}
                >
                  Common Fields Updated
                </Typography>

                {commonChanges.some((change: any) => change.isNewValuesAdded === true) && (
                  <>
                    <Typography
                      sx={{
                        color: "success.main",
                        fontStyle: "italic",
                        mb: 1,
                        fontSize: "12px",
                      }}
                    >
                      These are the new values being added
                    </Typography>

                    <TableContainer sx={{ mb: 0.75 }}>
                      <Table
                        size="small"
                        sx={denseTableSx}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>New Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {commonChanges.filter((change: any) => change.isNewValuesAdded === true)
                            .map((change: any, changeIndex: number) => (
                              <TableRow key={changeIndex}>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    color: "text.primary",
                                    verticalAlign: "top",
                                    pr: 1.5,
                                  }}
                                >
                                  {toTitleCase(change.field).replace(/_/g, " ")}
                                </TableCell>
                                <TableCell sx={{ verticalAlign: "top" }}>
                                  <Box
                                    sx={{
                                      p: 0.75,
                                      borderRadius: 1,
                                      backgroundColor: "#f0fdf4",
                                      border: "1px solid #bbf7d0",
                                      position: "relative",
                                      display: "inline-block",
                                      minWidth: 100,
                                    }}
                                  >
                                    <Chip
                                      label="NEW"
                                      size="small"
                                      sx={{
                                        position: "absolute",
                                        top: -8,
                                        left: 8,
                                        backgroundColor: "#16a34a",
                                        color: "white",
                                        fontSize: "9px",
                                        height: 14,
                                        "& .MuiChip-label": { px: 0.8 },
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        color: "#166534",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        mt: 0.5,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {String(change.updated)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {commonChanges.some((change: any) => change.isNewValuesAdded === false) && (
                  <>
                    <Typography
                      sx={{
                        color: "success.main",
                        fontStyle: "italic",
                        mb: 1,
                        fontSize: "12px",
                      }}
                    >
                      These are the old values being updated
                    </Typography>
                    <TableContainer>
                      <Table
                        size="small"
                        sx={{
                          ...denseTableSx,
                          "& .MuiTableCell-head": {
                            ...(denseTableSx["& .MuiTableCell-head"] as any),
                            fontSize: "12px",
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Previous Value</TableCell>
                            <TableCell>Updated Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {commonChanges.map((diff, index) => (
                            <TableRow key={index}>
                              <TableCell
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "12px",
                                  color: "text.primary",
                                  verticalAlign: "top",
                                  pr: 1.5,
                                }}
                              >
                                {toTitleCase(diff.field).replace(/_/g, " ")}
                              </TableCell>
                              <TableCell sx={{ verticalAlign: "top" }}>
                                <Box
                                  sx={{
                                    p: 0.9,
                                    borderRadius: 1,
                                    backgroundColor: "#fef2f2",
                                    border: "1px solid #fecaca",
                                    position: "relative",
                                  }}
                                >
                                  <Chip
                                    label="OLD"
                                    size="small"
                                    sx={{
                                      position: "absolute",
                                      top: -8,
                                      left: 8,
                                      backgroundColor: "#dc2626",
                                      color: "white",
                                      fontSize: "10px",
                                      height: 16,
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      color: "#991b1b",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      mt: 0.5,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {diff.previous
                                      ? String(diff.previous)
                                      : "---"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ verticalAlign: "top" }}>
                                <Box
                                  sx={{
                                    p: 0.9,
                                    borderRadius: 1,
                                    backgroundColor: "#f0fdf4",
                                    border: "1px solid #bbf7d0",
                                    position: "relative",
                                  }}
                                >
                                  <Chip
                                    label="NEW"
                                    size="small"
                                    sx={{
                                      position: "absolute",
                                      top: -8,
                                      left: 8,
                                      backgroundColor: "#16a34a",
                                      color: "white",
                                      fontSize: "10px",
                                      height: 16,
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      color: "#166534",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      mt: 0.5,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {diff.updated
                                      ? String(diff.updated)
                                      : "---"}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}

            {/* Event-Specific Changes */}
            {eventChanges.length > 0 && (
              <Box>
                {eventChanges.map((eventData, eventIndex) => (
                  <Box key={eventIndex} sx={{ mb: 1.75 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        color: eventData.isNewEvent
                          ? "success.main"
                          : "primary.main",
                        fontWeight: 700,
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        lineHeight: 1.2,
                      }}
                    >
                      Event {eventData.eventIndex + 1}: {eventData.eventName}
                      {eventData.isNewEvent && (
                        <Chip
                          label="NEW EVENT"
                          size="small"
                          color="success"
                          sx={{ ml: 0.5, height: 20 }}
                        />
                      )}
                    </Typography>

                    {eventData.isNewEvent && (
                      <Typography
                        sx={{
                          color: "success.main",
                          fontStyle: "italic",
                          mb: 1,
                          fontSize: "12px",
                        }}
                      >
                        This is a new event being added
                      </Typography>
                    )}

                    {eventData.changes.some((change: any) => change.isNewValuesAdded === true) && (
                      <>
                        <Typography
                          sx={{
                            color: "success.main",
                            fontStyle: "italic",
                            mb: 1,
                            fontSize: "12px",
                          }}
                        >
                          These are the new values being added
                        </Typography>

                        <TableContainer sx={{ mb: 0.75 }}>
                          <Table
                            size="small"
                            sx={denseTableSx}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell>Field</TableCell>
                                <TableCell>New Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {eventData.changes
                                .filter(
                                  (change: any) =>
                                    change.isNewValuesAdded === true,
                                )
                                .map((change: any, changeIndex: number) => (
                                  <TableRow key={changeIndex}>
                                    <TableCell
                                      sx={{
                                        fontWeight: 500,
                                        fontSize: "12px",
                                        color: "text.primary",
                                        verticalAlign: "top",
                                        pr: 1.5,
                                      }}
                                    >
                                      {toTitleCase(change.field).replace(
                                        /_/g,
                                        " ",
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: "top" }}>
                                      <Box
                                        sx={{
                                          p: 0.75,
                                          borderRadius: 1,
                                          backgroundColor: "#f0fdf4",
                                          border: "1px solid #bbf7d0",
                                          position: "relative",
                                          display: "inline-block",
                                          minWidth: 100,
                                        }}
                                      >
                                        <Chip
                                          label="NEW"
                                          size="small"
                                          sx={{
                                            position: "absolute",
                                            top: -8,
                                            left: 8,
                                            backgroundColor: "#16a34a",
                                            color: "white",
                                            fontSize: "9px",
                                            height: 14,
                                            "& .MuiChip-label": { px: 0.8 },
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            color: "#166534",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            mt: 0.5,
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {String(change.updated)}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}

                    {!eventData.isNewEvent &&
                      eventData.changes.some(
                        (change: any) => change.isNewValuesAdded === false,
                      ) && (
                        <>
                          <Typography
                            sx={{
                              color: "success.main",
                              fontStyle: "italic",
                              mb: 1,
                              fontSize: "12px",
                            }}
                          >
                            These are the old values being updated
                          </Typography>
                          <TableContainer>
                            <Table
                              size="small"
                              sx={{
                                ...denseTableSx,
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Field</TableCell>
                                  <TableCell>Previous Value</TableCell>
                                  <TableCell>Updated Value</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {eventData.changes
                                  .filter(
                                    (change: any) =>
                                      change.isNewValuesAdded === false,
                                  )
                                  .map((change: any, changeIndex: number) => (
                                    <TableRow key={changeIndex}>
                                      <TableCell
                                        sx={{
                                          fontWeight: 500,
                                          fontSize: "12px",
                                          color: "text.primary",
                                          verticalAlign: "top",
                                          pr: 1.5,
                                        }}
                                      >
                                        {toTitleCase(change.field).replace(
                                          /_/g,
                                          " ",
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: "top" }}>
                                        <Box
                                          sx={{
                                            p: 0.9,
                                            borderRadius: 1,
                                            backgroundColor: "#fef2f2",
                                            border: "1px solid #fecaca",
                                            position: "relative",
                                          }}
                                        >
                                          <Chip
                                            label="OLD"
                                            size="small"
                                            sx={{
                                              position: "absolute",
                                              top: -8,
                                              left: 8,
                                              backgroundColor: "#dc2626",
                                              color: "white",
                                              fontSize: "9px",
                                              height: 14,
                                              "& .MuiChip-label": { px: 0.8 },
                                            }}
                                          />
                                          <Typography
                                            sx={{
                                              color: "#991b1b",
                                              fontSize: "12px",
                                              fontWeight: 500,
                                              mt: 0.5,
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {String(change.previous)}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: "top" }}>
                                        <Box
                                          sx={{
                                            p: 0.9,
                                            borderRadius: 1,
                                            backgroundColor: "#f0fdf4",
                                            border: "1px solid #bbf7d0",
                                            position: "relative",
                                          }}
                                        >
                                          <Chip
                                            label="NEW"
                                            size="small"
                                            sx={{
                                              position: "absolute",
                                              top: -8,
                                              left: 8,
                                              backgroundColor: "#16a34a",
                                              color: "white",
                                              fontSize: "9px",
                                              height: 14,
                                              "& .MuiChip-label": { px: 0.8 },
                                            }}
                                          />
                                          <Typography
                                            sx={{
                                              color: "#166534",
                                              fontSize: "12px",
                                              fontWeight: 500,
                                              mt: 0.5,
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {String(change.updated)}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}

                    {eventIndex < eventChanges.length - 1 && (
                      <Divider sx={{ my: 1.5 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Typography>No changes detected</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};
